import type { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';
import { slugifyIntegration } from '@repo/shared/helpers';

import { algoliaClient, INDEX_MAP } from '#/lib/algolia';
import { fetchIntegrationProps } from '#/lib/sanity/fetchers/fetchIntegrationProps';
import type { NextPageData } from '#/lib/sanity/fetchers/helpers';
import { SanityIntegration } from '#/lib/sanity/queries/integration';
import { Skeleton } from '#/components/ui/skeleton';
import { IntegrationPage } from '#/components/IntegrationsPage';
import { BreadcrumbSchema } from '#/components/SeoSchema/BreadcrumbSchema';
import { IntegrationSchema } from '#/components/SeoSchema/IntegrationSchema';

interface AlgoliaIntegrationHit {
  href: string;
  objectID: string;
}

// We get the first page of integrations at build time and prerender them
export async function getStaticPaths() {
  try {
    // Query Algolia for the first 20 integrations
    const response = await algoliaClient.searchSingleIndex({
      indexName: INDEX_MAP.INTEGRATIONS,
      searchParams: {
        query: '',
        hitsPerPage: 20,
        attributesToRetrieve: ['href', 'objectID'],
      },
    });
    const firstResult = response.results?.[0];
    const hits = firstResult && 'hits' in firstResult ? firstResult.hits : [];

    // Extract slugs from the href field (format: '/integrations/slug')
    const paths = hits
      .map((hit: AlgoliaIntegrationHit) => {
        const href = hit.href;
        if (href) {
          const slug = href.replace('/integrations/', '');
          return { params: { slug } };
        }
        return null;
      })
      .filter(Boolean);

    // We'll pre-render only these paths at build time.
    // { fallback: 'blocking' } generates other routes on-demand with complete HTML.
    return { paths, fallback: 'blocking' };
  } catch (error) {
    console.error('Error fetching integration paths from Algolia:', error);
    // Return empty paths array if Algolia query fails
    return { paths: [], fallback: 'blocking' };
  }
}

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string }>) {
  let slug = params!.slug;

  const kebabCaseSlug = slugifyIntegration({ camelCaseIntegrationSlug: slug });
  if (kebabCaseSlug !== slug) {
    slug = kebabCaseSlug;
  }

  const props = await fetchIntegrationProps(slug, draftMode);

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props };
}

export default function Integration(
  props: NextPageData<typeof SanityIntegration>,
) {
  const router = useRouter();

  // Fallback skeleton for when the page is loading - only works when hard navigation is used - wont work when using next link
  if (router.isFallback) {
    return (
      <div>
        <div className="p-main mt-[calc(var(--header-height)*-1)] rounded-b-20 bg-grayscale-01 pb-40 pt-[calc(var(--header-height)+3rem)] text-center m:rounded-b-35 m:pb-100">
          <div className="mx-auto w-full max-w-735 opacity-50">
            {/* Logo skeleton */}
            <Skeleton className="mx-auto size-44 m:size-64 rounded-full" />

            {/* Title skeleton */}
            <Skeleton className="mx-auto mt-15 mb-12 h-20 w-200" />
            <Skeleton className="mx-auto mt-15 mb-12 h-20 w-200" />

            {/* Description skeleton */}
            <Skeleton className="mx-auto mt-45 mb-12 h-15 w-300" />
            <Skeleton className="mx-auto mt-15 mb-12 h-15 w-300" />
            <Skeleton className="mx-auto mt-15 mb-12 h-15 w-300" />

            {/* Button skeleton */}
            <Skeleton className="mx-auto mt-25 h-20 w-120 m:mt-30" />
          </div>
        </div>
      </div>
    );
  }

  if (!props.page) {
    return null;
  }

  return (
    <>
      <BreadcrumbSchema
        items={[
          { name: 'Integrations', path: '/integrations' },
          { name: props.page.title, path: props.page.href },
        ]}
      />
      <IntegrationSchema {...props.page} />
      <IntegrationPage
        page={props.page}
        site={props.site}
        isPreview={props.draftMode}
      />
    </>
  );
}
